import { useMemo } from "react";
import { LayoutDashboard, List, Building2, MapPin, Zap, Wifi, WifiOff, Activity, AlertTriangle } from "lucide-react";
import SectionLayout from "../../lib/components/SectionLayout";
import { useFleetData } from "../../lib/context/FleetDataProvider";
import useHierarchy from "../../lib/hooks/useHierarchy";
import { getCompanyDisplayName, isEnphaseUser } from "../../lib/utils/featureAccess";
const FLEET_TABS = [
  { to: "/fleet", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/fleet/list", label: "List", icon: List },
];

export default function FleetSection() {
  const { state: fleetState } = useFleetData();
  const enphaseUser = isEnphaseUser();
  const { stations: hierarchyStations } = useHierarchy({ enabled: !enphaseUser });

  const hierarchyStats = useMemo(() => {
    const incoming = Array.isArray(fleetState?.data) ? fleetState.data : [];
    const uniqueSites = new Set();

    incoming.forEach(charger => {
      if (charger.siteId) uniqueSites.add(charger.siteId);
    });

    const stationCount = hierarchyStations.filter(
      station => (station.stationName || station.name || "").toLowerCase() !== "unassigned"
    ).length;

    return {
      stations: stationCount,
      totalSites: uniqueSites.size,
    };
  }, [fleetState?.data, hierarchyStations]);

  const companyName = getCompanyDisplayName();

  const kpis = useMemo(() => {
    const data = Array.isArray(fleetState?.data) ? fleetState.data : [];
    if (!data.length) return null;
    const total = data.length;
    const online = data.filter(d => d.isConnected).length;
    const offline = total - online;
    const charging = data.filter(d => d.connectors?.some(c => c.connectorStatusType === "CHARGING")).length;
    const faulted = data.filter(d => d.isFaulted).length;
    return { total, online, offline, charging, faulted };
  }, [fleetState?.data]);

  const heroStats = kpis ? (
    <>
      {!enphaseUser && (
        <div className="hero-stat hero-stat--group">
          <span className="hero-stat__group-label">Stations</span>
          <div className="hero-stat__group-items">
            <div className="hero-stat__group-item">
              <Building2 size={12} className="hero-stat__icon hero-stat__icon--teal" />
              <span className="hero-stat__value">{hierarchyStats.stations}</span>
            </div>
          </div>
        </div>
      )}
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Sites</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <MapPin size={12} className="hero-stat__icon hero-stat__icon--purple" />
            <span className="hero-stat__value">{hierarchyStats.totalSites}</span>
          </div>
        </div>
      </div>
      <div className="hero-stat hero-stat--group">
        <span className="hero-stat__group-label">Chargers</span>
        <div className="hero-stat__group-items">
          <div className="hero-stat__group-item">
            <Zap size={12} className="hero-stat__icon hero-stat__icon--teal" />
            <span className="hero-stat__value">{kpis.total}</span>
            <span className="hero-stat__label">Total</span>
          </div>
          <div className="hero-stat__group-item">
            <Wifi size={12} className="hero-stat__icon hero-stat__icon--green" />
            <span className="hero-stat__value">{kpis.online}</span>
            <span className="hero-stat__label">Online</span>
          </div>
          <div className="hero-stat__group-item">
            <WifiOff size={12} className="hero-stat__icon hero-stat__icon--red" />
            <span className="hero-stat__value">{kpis.offline}</span>
            <span className="hero-stat__label">Offline</span>
          </div>
          <div className="hero-stat__group-item">
            <Activity size={12} className="hero-stat__icon hero-stat__icon--blue" />
            <span className="hero-stat__value">{kpis.charging}</span>
            <span className="hero-stat__label">Charging</span>
          </div>
          <div className="hero-stat__group-item">
            <AlertTriangle size={12} className="hero-stat__icon hero-stat__icon--amber" />
            <span className="hero-stat__value">{kpis.faulted}</span>
            <span className="hero-stat__label">Faulted</span>
          </div>
        </div>
      </div>
    </>
  ) : null;

  return (
    <SectionLayout
      companyName={companyName}
      title="Fleet Management"
      subtitle="Monitor and manage your entire EV charger fleet"
      tabs={FLEET_TABS}
      heroStats={heroStats}
    />
  );
}
